/**
 * Prijsgrafiek (US-022, US-023, design-handoff scherm Prijsgrafiek).
 *
 * De prijshistorie geeft het antwoord voor wie een precieze prijs op een precieze dag zoekt. Deze
 * pagina geeft het antwoord op de vraag ervóór: loopt de markt van ons weg, en bij welk product.
 * Daarom staat hier één groot diagram voor het gekozen product, en daaronder een klein diagram per
 * product waarmee je ziet waar je moet gaan kijken.
 *
 * De grafiek komt uit `/api/products/:id/historie` — dezelfde route als de tabel. Dat is met opzet:
 * die route past de eigen-prijsregel en de variantcontrole al toe, dus grafiek en tabel kunnen niet
 * uit elkaar lopen. Alleen de kaartjes onderaan hebben een eigen route, want die gaan over álle
 * producten.
 */

import {
  addDays,
  dagenMetWijziging,
  eersteWaarde,
  formatEuro,
  formatPercent,
  grootsteBeweging,
  laagsteMarktprijsPerDag,
  laatsteWaarde,
  toLocalDateKey,
  type Prijsreeks,
} from '@prijsmonitor/shared';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { api, type ApiError } from '../api/client.js';
import { Button, Notice, ui } from '../components/ui/index.js';
import { bereikVoor, PERIODES, type Periode } from '../lib/periode.js';
import { MiniGrafiek } from './MiniGrafiek.js';
import { PriceChart, type ChartSeries } from './PriceChart.js';
import styles from './PrijsgrafiekScreen.module.css';

interface HistoryCell {
  sourceId: string;
  priceCents: number | null;
}

interface History {
  productId: string;
  productName: string;
  ownSku: string;
  columns: Array<{ sourceId: string; name: string; isOwn: boolean }>;
  days: Array<{ dateKey: string; cells: HistoryCell[] }>;
}

interface Samenvatting {
  dagen: string[];
  producten: Array<{
    productId: string;
    name: string;
    ownSku: string;
    variant: string;
    eigen: Array<number | null>;
    laagste: Array<number | null>;
  }>;
}

interface Product {
  id: string;
  name: string;
  ownSku: string;
  lengthLabel: string | null;
  color: string | null;
}

export function PrijsgrafiekScreen(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState<Product[]>([]);
  const [history, setHistory] = useState<History | null>(null);
  const [samenvatting, setSamenvatting] = useState<Samenvatting | null>(null);
  const [verborgen, setVerborgen] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const productId = params.get('product') ?? '';
  const preset = (params.get('periode') ?? '30') as Periode;
  const relatief = params.get('modus') === 'relatief';

  const bereik = bereikVoor(preset) ?? {
    van: params.get('van') ?? addDays(toLocalDateKey(), -29),
    tot: params.get('tot') ?? toLocalDateKey(),
  };

  useEffect(() => {
    api
      .get<{ products: Product[] }>('/api/products?status=ACTIVE')
      .then((r) => {
        setProducts(r.products);
        if (!productId && r.products[0]) {
          setParams({ product: r.products[0].id }, { replace: true });
        }
      })
      .catch((e: ApiError) => setError(e.message));
    // Bewust alleen bij het openen: de productlijst verandert niet tijdens het kijken.
  }, []);

  const update = useCallback(
    (next: Record<string, string>) => {
      const query = new URLSearchParams(params);
      for (const [key, value] of Object.entries(next)) {
        if (value) query.set(key, value);
        else query.delete(key);
      }
      setParams(query, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    if (!productId) return;
    const query = new URLSearchParams({ van: bereik.van, tot: bereik.tot });
    api
      .get<History>(`/api/products/${productId}/historie?${query.toString()}`)
      .then(setHistory)
      .catch((e: ApiError) => setError(e.message));
  }, [productId, bereik.van, bereik.tot]);

  useEffect(() => {
    const query = new URLSearchParams({ van: bereik.van, tot: bereik.tot });
    api
      .get<Samenvatting>(`/api/prijsgrafiek/samenvatting?${query.toString()}`)
      .then(setSamenvatting)
      .catch((e: ApiError) => setError(e.message));
  }, [bereik.van, bereik.tot]);

  /*
   * De historie komt aflopend binnen (nieuwste dag eerst), want zo hoort de tabel te staan. Een
   * diagram leest van links naar rechts, dus hier gaat hij om.
   */
  const dagenOplopend = [...(history?.days ?? [])].reverse();

  const reeksen: ChartSeries[] = (history?.columns ?? []).map((column) => ({
    sourceId: column.sourceId,
    name: column.name,
    isOwn: column.isOwn,
    points: dagenOplopend.map((day) => ({
      dateKey: day.dateKey,
      priceCents: day.cells.find((c) => c.sourceId === column.sourceId)?.priceCents ?? null,
    })),
  }));

  // Zelfde vorm als de API voor de kaartjes gebruikt, zodat het rekenwerk uit `shared` komt.
  const prijsreeksen: Prijsreeks[] = reeksen.map((r) => ({
    sourceId: r.sourceId,
    name: r.name,
    isOwn: r.isOwn,
    waarden: r.points.map((p) => p.priceCents),
  }));

  const eigenReeks = prijsreeksen.find((r) => r.isOwn);
  const laagsteMarkt = laagsteMarktprijsPerDag(prijsreeksen);
  const eigenNu = eigenReeks ? laatsteWaarde(eigenReeks.waarden) : null;
  const marktNu = laatsteWaarde(laagsteMarkt);
  const beweging = grootsteBeweging(prijsreeksen);

  /*
   * Het verschil tussen de markt en ons, in procenten. Negatief betekent dat de goedkoopste
   * concurrent ónder onze prijs zit — dat is de melding waarvoor deze pagina bestaat, dus die krijgt
   * de opwaartse kleur.
   */
  const gat =
    eigenNu && marktNu && eigenNu.waarde !== 0
      ? ((marktNu.waarde - eigenNu.waarde) / eigenNu.waarde) * 100
      : null;

  const gekozenProduct = products.find((p) => p.id === productId);
  const variant = gekozenProduct
    ? [gekozenProduct.lengthLabel, gekozenProduct.color].filter(Boolean).join(' · ')
    : '';

  const kengetallen = [
    {
      label: 'Eigen prijs vandaag',
      waarde: eigenNu ? formatEuro(eigenNu.waarde) : '—',
      onder: [gekozenProduct?.ownSku, variant].filter(Boolean).join(' · '),
      kleur: 'var(--c-navy)',
    },
    {
      label: 'Laagste marktprijs nu',
      waarde: marktNu ? formatEuro(marktNu.waarde) : '—',
      onder:
        gat === null
          ? 'nog geen vergelijking mogelijk'
          : `markt ${formatPercent(Math.abs(gat))
              .replace('+', '')
              .replace('-', '')} ${gat >= 0 ? 'boven' : 'onder'} eigen prijs`,
      kleur: gat !== null && gat < 0 ? 'var(--c-up)' : 'var(--c-down)',
    },
    {
      label: 'Dagen met wijziging',
      waarde: String(dagenMetWijziging(prijsreeksen)),
      onder: `van ${dagenOplopend.length} ${dagenOplopend.length === 1 ? 'dag' : 'dagen'} met metingen`,
      kleur: 'var(--c-primary)',
    },
    {
      label: 'Grootste beweging',
      waarde: beweging ? formatPercent(beweging.procent) : '—',
      onder: beweging?.name ?? 'geen bron met twee metingen',
      kleur: beweging && beweging.procent > 0 ? 'var(--c-up)' : 'var(--c-down)',
    },
  ];

  /**
   * Naar de tabelweergave, met hetzelfde product en dezelfde periode.
   *
   * De datums gaan altijd mee, ook bij een periodeknop. De twee schermen delen die knoppen nu, dus
   * strikt genomen is de naam genoeg — maar een oude bladwijzer kan een sleutel bevatten die niet
   * meer bestaat, en dan zijn de datums het enige dat het bereik nog vasthoudt.
   */
  function naarHistorie(): void {
    const query = new URLSearchParams({
      product: productId,
      periode: preset,
      van: bereik.van,
      tot: bereik.tot,
    });
    void navigate(`/prijshistorie?${query.toString()}`);
  }

  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}

      <div className={styles.controls}>
        <select
          className={styles.select}
          value={productId}
          onChange={(e) => update({ product: e.target.value })}
          aria-label="Product"
        >
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {[p.name, p.lengthLabel, p.color].filter(Boolean).join(' · ')}
            </option>
          ))}
        </select>

        <div className={styles.presets} role="group" aria-label="Periode">
          {PERIODES.map((optie) => (
            <button
              key={optie.key}
              type="button"
              className={preset === optie.key ? styles.presetActive : styles.preset}
              onClick={() => update({ periode: optie.key, van: '', tot: '' })}
            >
              {optie.label}
            </button>
          ))}
        </div>

        <label className={styles.dateField}>
          van
          <input
            type="date"
            value={bereik.van}
            onChange={(e) => update({ periode: 'custom', van: e.target.value, tot: bereik.tot })}
          />
        </label>
        <label className={styles.dateField}>
          t/m
          <input
            type="date"
            value={bereik.tot}
            onChange={(e) => update({ periode: 'custom', van: bereik.van, tot: e.target.value })}
          />
        </label>

        <span className={styles.spacer} />

        <div className={styles.presets} role="group" aria-label="Weergave">
          <button
            type="button"
            className={relatief ? styles.preset : styles.presetActive}
            onClick={() => update({ modus: '' })}
          >
            Prijzen
          </button>
          <button
            type="button"
            className={relatief ? styles.presetActive : styles.preset}
            onClick={() => update({ modus: 'relatief' })}
          >
            % t.o.v. eigen prijs
          </button>
        </div>

        <Button onClick={naarHistorie} disabled={!productId}>
          Tabelweergave
        </Button>
      </div>

      <div className={styles.kengetallen}>
        {kengetallen.map((k) => (
          <div key={k.label} className={styles.kengetal}>
            <div className={styles.kengetalLabel}>{k.label}</div>
            <div className={styles.kengetalWaarde} style={{ color: k.kleur }}>
              {k.waarde}
            </div>
            <div className={styles.kengetalOnder}>{k.onder}</div>
          </div>
        ))}
      </div>

      <section className={styles.kaart}>
        <div className={styles.kaartKop}>
          <div>
            <div className={styles.kaartTitel}>
              {[history?.productName, variant].filter(Boolean).join(' · ')}
            </div>
            <div className={styles.kaartSub}>
              {relatief ? 'Afwijking van elke bron t.o.v. de eigen prijs' : 'Prijsverloop per bron'}{' '}
              · {dagenOplopend.length} {dagenOplopend.length === 1 ? 'dag' : 'dagen'} met metingen
            </div>
          </div>
        </div>

        {history && (
          <PriceChart
            series={reeksen}
            hidden={verborgen}
            variant="volledig"
            modus={relatief ? 'relatief' : 'absoluut'}
            onToggle={(sourceId) =>
              setVerborgen((vorig) => {
                const volgend = new Set(vorig);
                if (volgend.has(sourceId)) volgend.delete(sourceId);
                else volgend.add(sourceId);
                return volgend;
              })
            }
          />
        )}

        <p className={styles.voetnoot}>
          Lijnen tonen de gemeten prijs per bron per dag. Een dag zonder geldige meting onderbreekt
          de lijn — die dag is onbekend, niet gelijk gebleven. Klik een bron in de legenda om die
          tijdelijk te verbergen.
        </p>
      </section>

      <section className={styles.kaart}>
        <div className={styles.kaartKop}>
          <div className={styles.kaartTitel}>Alle producten in deze periode</div>
          <div className={styles.kaartSub}>
            Donker = eigen prijs · geel = laagste marktprijs. Klik een kaart om die bovenin te
            openen.
          </div>
        </div>

        {samenvatting && samenvatting.producten.length === 0 ? (
          <p className={ui.empty}>Er zijn nog geen actief gemonitorde producten.</p>
        ) : (
          <div className={styles.mini}>
            {(samenvatting?.producten ?? []).map((product) => {
              const eersteMarkt = eersteWaarde(product.laagste);
              const laatsteMarkt = laatsteWaarde(product.laagste);
              const verloop =
                eersteMarkt && laatsteMarkt && eersteMarkt.dag !== laatsteMarkt.dag
                  ? ((laatsteMarkt.waarde - eersteMarkt.waarde) / eersteMarkt.waarde) * 100
                  : null;
              const laatsteEigen = laatsteWaarde(product.eigen);
              const marktGoedkoper =
                laatsteMarkt !== null &&
                laatsteEigen !== null &&
                laatsteMarkt.waarde < laatsteEigen.waarde;

              return (
                <button
                  key={product.productId}
                  type="button"
                  className={
                    product.productId === productId ? styles.miniKaartActief : styles.miniKaart
                  }
                  onClick={() => update({ product: product.productId })}
                >
                  <div className={styles.miniKop}>
                    <div>
                      <div className={styles.miniNaam}>{product.name}</div>
                      <div className={styles.miniSub}>{product.variant || product.ownSku}</div>
                    </div>
                    <span
                      className={styles.miniVerloop}
                      style={{
                        color:
                          verloop === null || Math.abs(verloop) < 0.05
                            ? 'var(--c-text-secondary)'
                            : verloop > 0
                              ? 'var(--c-up)'
                              : 'var(--c-down)',
                      }}
                    >
                      {verloop === null
                        ? '—'
                        : `${verloop > 0.05 ? '▲ ' : verloop < -0.05 ? '▼ ' : ''}${formatPercent(verloop)}`}
                    </span>
                  </div>

                  <MiniGrafiek eigen={product.eigen} laagste={product.laagste} />

                  <div className={styles.miniVoet}>
                    {laatsteMarkt === null ? (
                      <span className={styles.miniStandGeen}>nog niet gemeten</span>
                    ) : (
                      <span
                        className={marktGoedkoper ? styles.miniStandMarkt : styles.miniStandEigen}
                      >
                        {marktGoedkoper ? 'Markt goedkoper' : 'Eigen prijs laagst'}
                      </span>
                    )}
                    <span className={styles.miniLaagst}>
                      {laatsteMarkt === null ? '' : `laagst: ${formatEuro(laatsteMarkt.waarde)}`}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
